import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Toast,
  ToastBody,
  ToastHeader,
  Alert,
  FormFeedback,
} from 'reactstrap';
import { logger } from '../../logging';
import { getDAMApp } from '../../services/firebase';
import { Partner } from './types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref } from '../../utils/firebase';
import { update, push, child } from 'firebase/database';
const R = require('ramda');

const partnersStyles = require('./partners.css');

export const PartnerModal = ({
  partner,
  isOpen,
  toggle,
  setPartner,
  user,
  isPartnersAdmin,
  partnersList,
}: {
  partner: Partner | null;
  isOpen: boolean;
  toggle: (isOpen: boolean) => void;
  setPartner: (partner: Partner | null) => void;
  user: any;
  isPartnersAdmin: boolean;
  partnersList?: any;
}) => {
  const damApp = getDAMApp();
  const functions = getFunctions(damApp);
  const [isNew, setIsNew] = useState(true);
  const [partnerName, setPartnerName] = useState('');
  const [partnerDescription, setPartnerDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [partnerType, setPartnerType] = useState<'internal' | 'external'>(
    'external'
  );
  const [saveModal, setSaveModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (partner === null) {
      setIsNew(true);
      setPartnerName('');
      setPartnerDescription('');
      setContactEmail('');
      setPartnerType('external');
      setNameError('');
      setEmailError('');
    } else {
      setIsNew(false);
      setPartnerName(partner.name || '');
      setPartnerDescription(partner.description || '');
      setContactEmail(partner.contact_email || '');
      setPartnerType(partner.partner_type || 'external');
      setNameError('');
      setEmailError('');
    }
  }, [partner, isOpen]);

  const handleClose = () => {
    setPartnerName('');
    setPartnerDescription('');
    setContactEmail('');
    setPartnerType('external');
    setSaving(false);
    setPartner(null);
    toggle(false);
    setNameError('');
    setEmailError('');
  };

  const validateName = (name: string) => {
    //console.log('partners list is:', partnersList);
    if (
      isNew &&
      partnersList &&
      partnersList.find(
        (p: Partner) => p.name.toLowerCase() === name.toLowerCase()
      )
    ) {
      return 'A partner with this name already exists.';
    }
    return '';
  };

  const validateEmail = (email: string) => {
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Invalid email format.';
    }
    return '';
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;

    if (newName.startsWith(' ') || newName.includes('  ')) {
      return;
    }

    setPartnerName(newName);
    setNameError(validateName(newName.trim()));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setContactEmail(newEmail);
    setEmailError(validateEmail(newEmail));
  };

  const toggleSaveModal = () => {
    setSaveModal(!saveModal);
  };

  const savePartner = async () => {
    logger.debug(`savePartner called - isNew: ${isNew}`);
    setSaving(true);
    const now = moment().unix();
    const validPartner: Partial<Partner> = {
      ...partner,
      name: partnerName.trim(),
      description: partnerDescription,
      contact_email: contactEmail,
      partner_type: partnerType,
      modified: now,
    };

    if (isNew) {
      try {
        validPartner['enabled'] = true;
        validPartner['created'] = now;
        validPartner['createdBy'] = { uid: user.uid, email: user.email };
        logger.info(
          `[savePartner] saving new validPartner: ${JSON.stringify(
            validPartner
          )}`
        );

        const newPartnerKey = await push(ref('partners')).key;
        validPartner['key'] = newPartnerKey;

        const partnerRef = ref(`partners/${newPartnerKey}`);
        await update(partnerRef, validPartner);

        logger.debug(`[savePartner] created partner`);
      } catch (e) {
        logger.error(`[savePartner] error saving new partner: ${e}`);
      }
    } else {
      try {
        const partnerRef = ref(`partners/${partner.key}`);
        logger.info(
          `saving existing validPartner: ${JSON.stringify(validPartner)}`
        );
        await update(partnerRef, validPartner);
      } catch (e) {
        logger.error(`[savePartner] error saving existing partner: ${e}`);
      }
    }

    logger.debug(`[savePartner] about to call handleClose()`);
    toggleSaveModal();
    handleClose();
  };

  const hasAnyErrors = Object.keys(errorMessages).length > 0;
  const saveDisabled =
    !partnerName ||
    !!nameError ||
    !contactEmail ||
    !!emailError ||
    saveModal ||
    hasAnyErrors ||
    saving;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      className={partnersStyles.partnerModal}
    >
      <ModalHeader
        toggle={handleClose}
        className={partnersStyles.partnerModalHeader}
      >
        {isNew === true
          ? 'Create New Partner'
          : `Edit Partner: ${partner && partner.name} (${
              partner && partner.key
            })`}
      </ModalHeader>
      <ModalBody className={partnersStyles.partnerModalBody}>
        {errorMessages && Object.keys(errorMessages).length > 0
          ? R.map((errorMessage: any) => {
              return (
                <Row key={errorMessage}>
                  <Col sm={12}>
                    <Alert color="danger">
                      {errorMessage && errorMessages[errorMessage].message}
                    </Alert>
                  </Col>
                </Row>
              );
            }, Object.keys(errorMessages))
          : null}
        <Form>
          {isNew === false && partner ? (
            <React.Fragment>
              <FormGroup row className={partnersStyles.partnerModalBodyRow}>
                <Label for="id" sm={2}>
                  ID
                </Label>
                <Col sm={10}>
                  <Input
                    type="text"
                    value={partner.key}
                    id="id"
                    disabled={true}
                  />
                </Col>
              </FormGroup>
              <FormGroup row className={partnersStyles.partnerModalBodyRow}>
                <Label for="created" sm={2}>
                  Created
                </Label>
                <Col sm={10}>
                  <Input
                    type="text"
                    value={
                      partner.created
                        ? moment
                            .unix(partner.created)
                            .format('YYYY-MM-DD HH:mm:ss')
                        : ''
                    }
                    id="created"
                    disabled={true}
                  />
                </Col>
              </FormGroup>
              <FormGroup row className={partnersStyles.partnerModalBodyRow}>
                <Label for="created_by" sm={2}>
                  Created By
                </Label>
                <Col sm={10}>
                  <Input
                    type="text"
                    value={partner.createdBy && partner.createdBy.email}
                    id="created_by"
                    disabled={true}
                  />
                </Col>
              </FormGroup>
              <FormGroup row className={partnersStyles.partnerModalBodyRow}>
                <Label for="modified" sm={2}>
                  Modified
                </Label>
                <Col sm={10}>
                  <Input
                    type="text"
                    value={
                      partner.modified
                        ? moment
                            .unix(partner.modified)
                            .format('YYYY-MM-DD HH:mm:ss')
                        : ''
                    }
                    id="modified"
                    disabled={true}
                  />
                </Col>
              </FormGroup>
            </React.Fragment>
          ) : null}
          <FormGroup row className={partnersStyles.partnerModalBodyRow}>
            <Label for="name" sm={2}>
              Name
            </Label>
            <Col sm={10}>
              <Input
                type="text"
                value={partnerName}
                id="name"
                onChange={handleNameChange}
                invalid={!!nameError}
              />
              <FormFeedback>{nameError}</FormFeedback>
            </Col>
          </FormGroup>
          <FormGroup row className={partnersStyles.partnerModalBodyRow}>
            <Label for="description" sm={2}>
              Description
            </Label>
            <Col sm={10}>
              <Input
                type="textarea"
                value={partnerDescription}
                id="description"
                onChange={(e) => setPartnerDescription(e.target.value)}
              />
            </Col>
          </FormGroup>
          <FormGroup row className={partnersStyles.partnerModalBodyRow}>
            <Label for="contact_email" sm={2}>
              Contact Email
            </Label>
            <Col sm={10}>
              <Input
                type="email"
                value={contactEmail}
                id="contact_email"
                onChange={handleEmailChange}
                invalid={!!emailError}
              />
              <FormFeedback>{emailError}</FormFeedback>
            </Col>
          </FormGroup>
          <FormGroup row className={partnersStyles.partnerModalBodyRow}>
            <Label for="partner_type" sm={2}>
              Partner Type
            </Label>
            <Col sm={10}>
              <Input
                type="select"
                value={partnerType}
                id="partner_type"
                onChange={(e) =>
                  setPartnerType(e.target.value as 'internal' | 'external')
                }
              >
                <option value="external">External</option>
                <option value="internal">Internal</option>
              </Input>
            </Col>
          </FormGroup>
          <Row>
            <Col>
              <Button disabled={saveDisabled} onClick={toggleSaveModal}>
                Save
              </Button>
            </Col>
          </Row>
        </Form>
        <Row>
          <Modal isOpen={saveModal} toggle={(e) => toggleSaveModal()}>
            <ModalHeader toggle={(e) => toggleSaveModal()}>
              Please Confirm Save
            </ModalHeader>
            <ModalBody>{`Please confirm that you'd like to save the partner.`}</ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                onClick={async () => await savePartner()}
                disabled={saving}
              >
                Confirm
              </Button>{' '}
              <Button
                color="secondary"
                onClick={(e) => toggleSaveModal()}
                disabled={saving}
              >
                Cancel
              </Button>
            </ModalFooter>
          </Modal>
        </Row>
      </ModalBody>
    </Modal>
  );
};
